import React, { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, XCircle, DollarSign, Download, Loader2, List } from 'lucide-react';
import { BulkVehicleReportResponse, BulkVehicleReportItem, extractVehicleInfo } from '../services/vehicleReportService';
import VehicleOverviewCard from './VehicleOverviewCard';
import VehicleBrandsSection from './VehicleBrandsSection';

interface BulkReportResultsProps {
  results: BulkVehicleReportResponse;
  showPricing?: boolean;
  onGeneratePdf: (showBrandChecklist: boolean) => void;
  isGeneratingPdf: boolean;
}

const BulkReportResults: React.FC<BulkReportResultsProps> = ({
  results,
  showPricing = true,
  onGeneratePdf,
  isGeneratingPdf,
}) => {
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [showBrandChecklist, setShowBrandChecklist] = useState(false);

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const successfulResults = results.results.filter(r => r.success);

  return (
    <div className="space-y-4">
      {/* Summary Banner */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl border border-black/[0.08] p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-[#64748b]">Total:</span>
              <span className="font-bold text-[#0f172a]">{results.totalRequested}</span>
            </div>
            <div className="flex items-center gap-2 text-[#16a34a]">
              <CheckCircle2 className="w-4 h-4" />
              <span>{results.totalSucceeded} succeeded</span>
            </div>
            {results.totalFailed > 0 && (
              <div className="flex items-center gap-2 text-[#dc2626]">
                <XCircle className="w-4 h-4" />
                <span>{results.totalFailed} failed</span>
              </div>
            )}
            {showPricing && results.totalBillingAmount > 0 && (
              <div className="flex items-center gap-2 text-[#d97706]">
                <DollarSign className="w-4 h-4" />
                <span>{formatCurrency(results.totalBillingAmount)}</span>
              </div>
            )}
          </div>

          {/* Generate PDF Button & Brand Checklist Option */}
          {successfulResults.length > 0 && (
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={showBrandChecklist}
                  onChange={(e) => setShowBrandChecklist(e.target.checked)}
                  disabled={isGeneratingPdf}
                  className="w-4 h-4 rounded border-black/20 text-[#c41e3a] focus:ring-[#c41e3a] focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <List className="w-4 h-4 text-[#64748b] group-hover:text-[#0f172a] transition-colors" />
                <span className="text-sm text-[#64748b] group-hover:text-[#0f172a] transition-colors">
                  Include brand checklist
                </span>
              </label>
              <button
                onClick={() => onGeneratePdf(showBrandChecklist)}
                disabled={isGeneratingPdf}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#c41e3a] to-[#a01830] hover:from-[#a01830] hover:to-[#8a1428] text-white text-sm font-bold rounded-lg transition-all shadow-lg shadow-[#c41e3a]/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPdf ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Generate Combined PDF</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results List */}
      <div className="space-y-3">
        {results.results.map((item, index) => (
          <ResultItem
            key={index}
            item={item}
            isExpanded={expandedItems.has(index)}
            onToggle={() => toggleExpanded(index)}
            showPricing={showPricing}
          />
        ))}
      </div>
    </div>
  );
};

interface ResultItemProps {
  item: BulkVehicleReportItem;
  isExpanded: boolean;
  onToggle: () => void;
  showPricing?: boolean;
}

const ResultItem: React.FC<ResultItemProps> = ({
  item,
  isExpanded,
  onToggle,
  showPricing = true,
}) => {
  const vehicleInfo = item.success && item.report?.data
    ? extractVehicleInfo(item.report.data, item.report.stolenRecords)
    : null;

  const vehicleDescription = item.report
    ? [item.report.vehicleYear, item.report.vehicleMake, item.report.vehicleModel]
        .filter(Boolean)
        .join(' ')
    : '';

  return (
    <div className={`bg-white/80 backdrop-blur-sm rounded-xl border overflow-hidden transition-all shadow-sm ${
      item.success ? 'border-black/[0.08]' : 'border-[#dc2626]/30'
    }`}>
      {/* Header Row */}
      <button
        onClick={onToggle}
        disabled={!item.success}
        className={`w-full px-4 py-3 flex items-center justify-between text-left transition-all ${
          item.success
            ? 'hover:bg-black/[0.02] cursor-pointer'
            : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-4 flex-1 min-w-0">
          {/* Index Number */}
          <span className="text-[#94a3b8] text-sm font-medium w-6 text-center flex-shrink-0">
            {item.index + 1}.
          </span>

          {/* VIN */}
          <span className="font-mono text-sm text-[#0f172a] font-medium">
            {item.vin}
          </span>

          {/* Vehicle Description */}
          {vehicleDescription && (
            <span className="text-sm text-[#64748b] truncate hidden sm:block">
              {vehicleDescription}
            </span>
          )}

          {/* Reference Note */}
          {item.referenceNote && (
            <span className="text-xs text-[#94a3b8] truncate hidden md:block">
              Ref: {item.referenceNote}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Status Badge */}
          {item.success ? (
            <div className="flex items-center gap-2">
              {showPricing && item.report?.billingAmount != null && item.report.billingAmount > 0 && (
                <span className="px-2 py-1 bg-[#d97706]/10 text-[#d97706] text-xs font-medium rounded">
                  ${item.report.billingAmount.toFixed(2)}
                </span>
              )}
              <span className="px-2 py-1 bg-[#16a34a]/10 text-[#16a34a] text-xs font-medium rounded">
                Success
              </span>
              {/* Expand/Collapse Indicator */}
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-[#94a3b8]" />
              ) : (
                <ChevronDown className="w-4 h-4 text-[#94a3b8]" />
              )}
            </div>
          ) : (
            <span className="px-2 py-1 bg-[#dc2626]/10 text-[#dc2626] text-xs font-medium rounded flex items-center gap-1">
              <XCircle className="w-3 h-3" />
              Failed
            </span>
          )}
        </div>
      </button>

      {/* Error Message for Failed Items */}
      {!item.success && item.errorMessage && (
        <div className="px-4 pb-3 text-sm text-[#dc2626]">
          {item.errorMessage}
        </div>
      )}

      {/* Expanded Content for Successful Items */}
      {item.success && isExpanded && item.report && vehicleInfo && (
        <div className="border-t border-black/[0.06] p-4 space-y-4">
          {/* Vehicle Overview */}
          <VehicleOverviewCard
            vehicleInfo={vehicleInfo}
          />

          {/* Title History Summary */}
          {vehicleInfo.titles && vehicleInfo.titles.length > 0 && (
            <div className="bg-[#f8fafc] rounded-lg p-4 border border-black/[0.05]">
              <h4 className="text-sm font-medium text-[#0f172a] mb-3">Title History</h4>
              <div className="space-y-2">
                {vehicleInfo.titles.slice(0, 5).map((title, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs ${
                      title.isHistoric
                        ? 'bg-[#e2e8f0] text-[#64748b]'
                        : 'bg-[#16a34a]/10 text-[#16a34a]'
                    }`}>
                      {title.isHistoric ? 'Historic' : 'Current'}
                    </span>
                    <span className="text-[#0f172a]">{title.state}</span>
                    <span className="text-[#64748b]">
                      {title.date ? new Date(title.date).toLocaleDateString() : 'N/A'}
                    </span>
                    <span className="text-[#94a3b8]">
                      {title.odometer ? `${title.odometer.toLocaleString()} mi` : 'EXEMPT'}
                    </span>
                  </div>
                ))}
                {vehicleInfo.titles.length > 5 && (
                  <div className="text-xs text-[#94a3b8]">
                    +{vehicleInfo.titles.length - 5} more title records
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Brands Summary */}
          {vehicleInfo.brands && vehicleInfo.brands.length > 0 && (
            <VehicleBrandsSection
              brands={vehicleInfo.brands}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BulkReportResults;
